import Link from 'next/link';
import { ArticleCard } from '@/components/article-card';
import { type Article } from '@/lib/types';
import { getCategoryUrl } from '@/lib/articles';
import { cn } from '@/lib/utils';
import React from 'react';
import { ChevronRight } from 'lucide-react';

interface NewsSectionProps {
    title: string;
    articles: Article[];
    showReadMore?: boolean;
    categoryId?: string;
    subcategory?: string;
    hideLede?: boolean;
    hideCategory?: boolean;
    layout?: 'grid' | 'horizontal-list';
}

const getMobileLayout = (idx: number) => {
  const r = idx % 7;
  if (r === 0) return 'overlay';
  if (r === 3) return 'default';
  return 'side-mobile';
};

/**
 * NewsSection Component
 * Investigative Stage Implementation for all secondary sections.
 */
export function NewsSection({ title, articles, categoryId, subcategory, hideLede = false, hideCategory = false, layout = 'grid' }: NewsSectionProps) {
    if (articles.length === 0) {
        return null;
    }

    const isSecurity = (categoryId || '').toLowerCase().includes('security');
    const isEducation = (categoryId || '').toLowerCase().includes('education');
    
    return (
        <section 
            className="mb-16 xl:mb-24" 
            style={{ 
                contentVisibility: 'auto', 
                containIntrinsicSize: '0 500px' 
            }}
        >
            <div className="flex items-center gap-3 mb-6 py-1 px-6 md:px-0">
                {categoryId ? (
                    <Link href={getCategoryUrl(categoryId, subcategory)} className="inline-flex items-center gap-1 group">
                        <h2 className="text-2xl md:text-xl font-medium capitalize tracking-tight text-[hsl(var(--section-header))] font-headline">{title}</h2>
                        <ChevronRight className="h-5 w-5 text-[hsl(var(--section-header))] group-hover:translate-x-0.5 transition-transform" />
                    </Link>
                ) : (
                    <h2 className="text-2xl md:text-xl font-medium capitalize tracking-tight text-[hsl(var(--section-header))] font-headline">{title}</h2>
                )}
            </div>
            <div className="md:bg-[hsl(var(--investigative-stage-bg))] md:p-8 lg:px-10 lg:py-14 md:rounded-[3rem] md:shadow-2xl overflow-hidden">
                <div className="p-0 md:p-0">
                    <div className={cn(
                        "grid gap-x-2 gap-y-2 md:gap-y-3 items-stretch w-full",
                        (layout === 'horizontal-list' || isSecurity)
                            ? "grid-cols-1" 
                            : isEducation 
                                ? "grid-cols-1 md:grid-cols-2"
                                : "grid-cols-1 md:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6"
                    )}>
                        {articles.map((article, index) => {
                            let cardLayout: any = 'default';

                            if (isSecurity) {
                                cardLayout = 'horizontal-list-left';
                            } else if (layout === 'horizontal-list') {
                                cardLayout = 'horizontal-list';
                            }

                            return (
                                <div 
                                    key={`${article.id}-${article.publishDate}-${index}`} 
                                    className={cn(
                                        "h-full",
                                        (isSecurity || layout === 'horizontal-list') && index > 0 && "pt-1 md:pt-2"
                                    )}
                                >
                                    <ArticleCard 
                                        article={article} 
                                        layout={cardLayout}
                                        mobileLayout={getMobileLayout(index)}
                                        hideLede={hideLede}
                                        hideCategory={hideCategory}
                                        className="h-full md:bg-transparent md:shadow-none md:border-none md:p-0"
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
